import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import YouTubeIcon from '@mui/icons-material/YouTube';
import fullLogo from '../../assets/FullLogo-WhiteText.svg';
import dpgBadge from '../../assets/dpg-badge-w.svg';
import { PRIMARY_COLORS, WHITE } from '../../common/colors';
import { CHAT_URL, CONTACT_EMAIL, GITHUB_URL } from '../../common/constants';
import { SITE } from '../common/PageMeta';

// Footer (ocl_online#154): full logo + DPG badge on the left, three link
// columns (site, tools & community, legal) with the social icons under them
// on the right, copyright bar at the bottom.
const linkColumns = t => [
  {
    links: [
      { label: t('nav.tools'), to: '/tools' },
      { label: t('nav.blog'), to: '/blog' },
      { label: t('footer.about_us'), to: '/about-us' },
      { label: t('footer.faq'), to: '/faq' },
      { label: t('footer.roadmap'), to: '/blog/2025-ocl-community-roadmap' },
    ],
  },
  {
    links: [
      { label: t('footer.termbrowser'), to: '/tools/termbrowser' },
      { label: t('footer.terminology_service'), to: '/tools/terminology-service' },
      { label: t('footer.mapper'), to: '/tools/mapper' },
      { label: t('nav.chat'), href: CHAT_URL },
      // GitHub moved here from the row above the footer (ocl_online#170).
      { label: t('footer.github'), href: GITHUB_URL },
      { label: t('footer.contact_us'), href: `mailto:${CONTACT_EMAIL}` },
    ],
  },
  // CS-11 decision: link the real legal documents (the design's
  // Privacy/Terms/Cookies were '#' placeholders; there is no separate
  // cookie policy — cookies are covered in the privacy policy).
  // Terms of Use added for ocl_online#165.
  {
    links: [
      { label: t('footer.privacy'), to: '/privacy-policy' },
      { label: t('footer.terms'), to: '/terms-of-use' },
      { label: t('footer.license'), to: '/license' },
      { label: t('footer.code_of_conduct'), to: '/code-of-conduct' },
    ],
  },
];

// Exported so JsonLd's site-wide Organization schema (Layout.jsx) can
// reuse the same URLs for `sameAs` instead of a second hardcoded copy.
export const SOCIAL_LINKS = [
  { label: 'LinkedIn', icon: LinkedInIcon, href: 'https://www.linkedin.com/company/open-concept-lab' },
  { label: 'YouTube', icon: YouTubeIcon, href: 'https://www.youtube.com/@openconceptlab' },
];

// DPGA's badge guide asks verified DPGs to link the badge to their registry profile.
const DPG_REGISTRY_URL = 'https://www.digitalpublicgoods.net/r/open-concept-lab';

const FooterLink = ({ label, to, href, ...rest }) => (
  <Link
    {...(to ? { component: RouterLink, to } : { href })}
    underline="hover"
    sx={{ color: WHITE, fontSize: '14px' }}
    {...rest}>
    {label}
  </Link>
);

const LinkColumn = ({ title, links }) => (
  <Stack spacing={1.5}>
    {title && (
      <Typography variant="body2" sx={{ color: WHITE, fontWeight: 600 }}>
        {title}
      </Typography>
    )}
    {links.map(link => <FooterLink key={link.label} {...link} />)}
  </Stack>
);

const Footer = () => {
  const { t } = useTranslation();
  return (
    <Box component="footer" sx={{ backgroundColor: PRIMARY_COLORS['10'] }}>
      <Container maxWidth="lg">
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
        <Box sx={{ py: { xs: 6, md: 8 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 6 }}>
          <Stack spacing={3} sx={{ alignItems: 'flex-start' }}>
            <Box component="img" src={fullLogo} alt="Open Concept Lab" sx={{ display: 'block', height: 70 }} />
            <Link href={DPG_REGISTRY_URL} target="_blank" rel="noreferrer noopener" underline="none" sx={{ display: 'block', lineHeight: 0 }}>
              <Box component="img" src={dpgBadge} alt={t('footer.dpg_badge')} sx={{ display: 'block', height: 28 }} />
            </Link>
          </Stack>
          {/* A flex Box rather than a Stack: Stack zeroes its children's margins, which would cancel the icon row's offset. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box component="nav" aria-label={t('footer.nav_aria')} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 4 }}>
              {linkColumns(t).map((column, i) => <LinkColumn key={i} {...column} />)}
            </Box>
            {/* The negative margin offsets the small IconButtons' padding so the icons line up with the links above. */}
            <Stack direction="row" spacing={1} sx={{ ml: '-5px' }}>
              {SOCIAL_LINKS.map(({ label, icon: Icon, href }) => (
                <IconButton key={label} aria-label={label} href={href} size="small" sx={{ color: WHITE }} component='a' rel='noreferrer noopener' target='_blank'>
                  <Icon fontSize="small" />
                </IconButton>
              ))}
              <IconButton aria-label={t('footer.rss_feed')} href={`${SITE}/feed/`} size="small" sx={{ color: WHITE }} component='a'>
                <RssFeedIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
        <Box sx={{ py: 3 }}>
          <Typography variant="body2" sx={{ color: WHITE }}>
            © {new Date().getFullYear()} Open Concept Lab. {t('footer.rights')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
